import { DocumentView } from '@carbon/react/icons';
import type { DatasetConfig } from '../types';

// Documents plot as clustered centroid dots at overview zoom (a `centroid`
// geo_point circle layer from z0), with the buffered geo_shape `geometry` fill
// footprint filling in once zoomed in (z8). `documents` is already a resources
// key (Search Documents), so we POST directly via directHref with the Map Query
// filter params.
export const documentsConfig: DatasetConfig = {
  id: 'documents',
  usesGlobalText: true,
  label: 'Documents',
  icon: DocumentView,
  defaultFilterCategory: 'Document Type',
  searchEndpoint: '/queries/documents',
  // Joins the Map Query spatial-export roster as the backend 'documents' dataset. Attribute ids match
  // GisExport::Sources::MapQuery::DOCUMENT_COLUMNS (the dialog offers only backend-supported attributes).
  spatialExport: {
    key: 'documents',
    attributes: [
      { id: 'document_id', label: 'Document ID' },
      { id: 'book_type', label: 'Book Type' },
      { id: 'volume', label: 'Volume' },
      { id: 'page', label: 'Page' },
      { id: 'instrument_number', label: 'Instrument Number' },
      { id: 'state', label: 'State' },
      { id: 'county', label: 'County' },
    ],
  },
  directHref: '/queries/documents',
  tileEndpoint: '/queries/documents/mvt',
  // Matches the rendered documents fill (Carbon green 50).
  defaultLayerColor: '#24a148',
  // Dots are document centroids; a document covering a large area is centered
  // elsewhere, so the map can plot fewer dots than the result count.
  layerNote:
    'Dots mark document centroids; large-area documents may be centered outside the view, so the map can show fewer dots than the result count.',
  // Attributes present as MVT tile properties, so color rules recolor the map.
  colorByAttributes: [
    {
      facetKey: 'documentType',
      label: 'Document Type',
      mvtProperty: 'type',
      // STI category (documentTypeKey), not the free-text documentType column, so
      // the card match uses the same value as the facet and the tile `type`.
      rowField: 'documentTypeKey',
    },
    {
      facetKey: 'documentSubtype',
      label: 'Document Subtype',
      mvtProperty: 'document_subtype',
      rowField: 'documentSubtype',
    },
    // "State" lives on the modal's Common tab (sourced from the wells facets, whose
    // ids are state abbreviations). state_abbreviation is a scalar tile prop + a
    // row field, so it recolors the map + cards, matched by abbreviation.
    {
      facetKey: 'state',
      label: 'State',
      mvtProperty: 'state_abbreviation',
      rowField: 'stateAbbreviation',
    },
    // "Operator" is derived from each document's linked wells — a set, not a
    // scalar. The row carries the array (any-match card accent); the tile carries
    // a delimiter-wrapped string matched by "contains" (multiValued).
    {
      facetKey: 'currentOperator',
      label: 'Operator',
      mvtProperty: 'operators_mvt',
      rowField: 'operators',
      multiValued: true,
    },
    // "Provisioning Status" bundles the provisioning and structuring axes
    // (row.statuses) behind one facet. Each axis is a scalar tile property
    // (provisioning_status, structuring_status), so all four values recolor the
    // map via valueTileMap.
    {
      facetKey: 'statuses',
      label: 'Provisioning Status',
      rowField: 'statuses',
      valueTileMap: {
        provisioned: {
          mvtProperty: 'provisioning_status',
          mvtValue: 'provisioned',
        },
        unprovisioned: {
          mvtProperty: 'provisioning_status',
          mvtValue: 'not_provisioned',
        },
        // Structuring wins over provisioning on overlap: a provisioned +
        // structured document colors as Structured, not Provisioned.
        structured: {
          mvtProperty: 'structuring_status',
          mvtValue: 'structured',
          precedence: 1,
        },
        unstructured: {
          mvtProperty: 'structuring_status',
          mvtValue: 'unstructured',
          precedence: 1,
        },
      },
    },
  ],
  // Recording-county plotting: the sidebar "Plot by" control lets documents
  // match the map extent/AOI by their recording county instead of their
  // survey-area centroid.
  supportsCountyPlot: true,
  // Runsheet card layout; selecting a card highlights the matching map footprint
  // (by document id) and flies to the card's representative point.
  resultsLayout: 'cards',
  highlightRowKey: 'id',
  highlightFeatureKey: 'id',
  flyToKey: 'mapPoint',
  // Facets are resolved from PostgreSQL and are the slow part of this endpoint;
  // fetch them in a dedicated request so map panning / result paging stay fast.
  facetsMode: 'separate',
  // Lean: only the facets the filters read, and no hits, so no sort.
  facetsPayload: { response_config: { lean_facets: true } },
  // The text search is Elastic simple_query_string (a space is OR), which the
  // Search Tips describe; `+` lets the dataset box narrow the global text.
  narrowsGlobalText: true,
  showSearchTips: true,
  alphabeticalFacetOptions: true,
  defaultPayload: {
    // A present `text` key routes Query::Documents to the Elastic strategy.
    text: '',
    page: 1,
    pageSize: 100,
    // Results only; facets come from the separate request above. The extent
    // stays for FitBoundsOnSearch (No Map Filter and AOI), and lean_cards skips
    // the citations, resource links and push channel the cards never read.
    response_config: {
      include_facets: false,
      include_bounding_box: true,
      lean_cards: true,
    },
  },

  filters: [
    // ── Provisioning Status (Provisioned/Unprovisioned + Structured/Unstructured) ──
    {
      id: 'statuses',
      label: 'Provisioning Status',
      category: 'Provisioning Status',
      type: 'multi-select-list',
      param: 'statuses',
      facetKey: 'statuses',
    },

    // ── Conveyance Type ──
    {
      id: 'conveyance_types',
      label: 'Conveyance Type',
      category: 'Conveyance Type',
      type: 'multi-select-list',
      param: 'conveyance_types',
      facetKey: 'conveyanceType',
    },

    // ── Document Subtype ──
    {
      id: 'document_subtypes',
      label: 'Document Subtype',
      category: 'Document Subtype',
      type: 'multi-select-list',
      param: 'document_subtypes',
      facetKey: 'documentSubtype',
    },

    // ── Document Type ──
    {
      id: 'document_types',
      label: 'Document Type',
      category: 'Document Type',
      type: 'multi-select-list',
      param: 'document_types',
      facetKey: 'documentType',
    },

    // ── Assumption (All / Yes / No) ──
    {
      id: 'assumption',
      label: 'Assumption',
      category: 'Assumption',
      type: 'radio',
      param: 'assumption',
      facetKey: 'assumption',
    },

    // ── Parties ── typeahead over grantor/grantee entities (id filter) with a
    // free-text name fallback for unstructured, entity-less parties.
    {
      id: 'conveyor',
      label: 'Conveyor',
      category: 'Parties',
      type: 'multi-select-text-search-combo',
      param: 'conveyor_ids',
      textParam: 'conveyor',
      facetKey: 'conveyor',
      selectAll: true,
    },
    {
      id: 'conveyee',
      label: 'Conveyee',
      category: 'Parties',
      type: 'multi-select-text-search-combo',
      param: 'conveyee_ids',
      textParam: 'conveyee',
      facetKey: 'conveyee',
      selectAll: true,
    },

    // ── Legal Description ──
    {
      id: 'survey_area',
      label: 'Survey Area',
      category: 'Legal Description',
      type: 'survey-area',
      param: 'survey_area',
      placeholder: 'e.g. TX-Martin-T&P RR CO-37 T1S-5',
    },

    // ── Property - Wellbore ──
    {
      id: 'wellbore',
      label: 'Documents with Property - Wellbore',
      category: 'Property - Wellbore',
      type: 'checkbox',
      param: 'wellbore',
    },

    // ── State / County ── one expandable tree: states, each expanding to its
    // counties. State and county are independent id filters.
    {
      id: 'state_county',
      label: 'State / County',
      category: 'State / County',
      type: 'tree-table',
      param: 'state_ids',
      facetKey: 'state',
      childParam: 'county_ids',
      childFacetKey: 'county',
    },

    // ── Date Filters ──
    {
      id: 'instrument_date',
      label: 'Instrument Date',
      category: 'Date Filters',
      type: 'date-range',
      minParam: 'min_instrument_date',
      maxParam: 'max_instrument_date',
    },
    {
      id: 'effective_date',
      label: 'Effective Date',
      category: 'Date Filters',
      type: 'date-range',
      minParam: 'min_effective_date',
      maxParam: 'max_effective_date',
    },
    {
      id: 'file_date',
      label: 'File Date',
      category: 'Date Filters',
      type: 'date-range',
      minParam: 'min_file_date',
      maxParam: 'max_file_date',
    },
    {
      id: 'recorded_date',
      label: 'Recorded Date',
      category: 'Date Filters',
      type: 'date-range',
      minParam: 'min_recorded_date',
      maxParam: 'max_recorded_date',
    },
    {
      id: 'created_at',
      label: 'Created',
      category: 'Date Filters',
      type: 'date-range',
      minParam: 'min_created_at',
      maxParam: 'max_created_at',
    },
    {
      id: 'updated_at',
      label: 'Updated',
      category: 'Date Filters',
      type: 'date-range',
      minParam: 'min_updated_at',
      maxParam: 'max_updated_at',
    },
  ],

  columns: [
    {
      id: 'title',
      header: 'Document',
      accessorKey: 'title',
      size: 260,
      sortable: false,
      type: 'link',
      hrefKey: 'href',
      wrap: true,
    },
    {
      id: 'document_type',
      header: 'Type',
      accessorKey: 'documentType',
      size: 150,
      sortable: false,
    },
    {
      id: 'document_subtype',
      header: 'Subtype',
      accessorKey: 'documentSubtype',
      size: 200,
      sortable: false,
      wrap: true,
    },
    {
      id: 'instrument_date',
      header: 'Instrument Date',
      accessorKey: 'instrumentDate',
      size: 180,
      sortable: true,
      type: 'date',
    },
    {
      id: 'effective_date',
      header: 'Effective Date',
      accessorKey: 'effectiveDate',
      size: 180,
      sortable: true,
      type: 'date',
    },
    {
      id: 'recorded_date',
      header: 'Recorded Date',
      accessorKey: 'recordedDate',
      size: 180,
      sortable: true,
      type: 'date',
    },
    {
      id: 'county',
      header: 'County',
      accessorKey: 'countyName',
      size: 160,
      sortable: false,
    },
    {
      id: 'state',
      header: 'State',
      accessorKey: 'stateAbbreviation',
      size: 120,
      sortable: false,
    },
  ],

  tileLayers: [
    // Centroid point clusters at every zoom (like Wells' surface hole) so
    // documents show at the overview; the geo_shape footprint fills in at zoom 8.
    {
      geoAttribute: 'centroid',
      layerType: 'circle',
      minZoom: 0,
      // Co-located deeds collapse onto one centroid; expose the stack count so
      // the hover popup can show "+N more here" for a single dot.
      clusterCount: true,
    },
    {
      geoAttribute: 'geometry',
      layerType: 'fill',
      minZoom: 8,
      // Deeds stack 100s-deep on the same sections; fill only the selected
      // footprint so the map doesn't flood solid green.
      fillOnlyHighlighted: true,
    },
  ],
};
